<template>
<div class="camera-verification-container">
  <div class="camera-container position-relative">
    <!-- New Loader Overlay -->
    <div v-if="isLoading" class="facemesh-loader-overlay">
      <div class="facemesh-loader-container">
        <!-- Face Animation -->
        <div class="facemesh-face-container">
          <!-- Corners -->
          <div class="facemesh-corner facemesh-top-left"></div>
          <div class="facemesh-corner facemesh-top-right"></div>
          <div class="facemesh-corner facemesh-bottom-left"></div>
          <div class="facemesh-corner facemesh-bottom-right"></div>
          
          <!-- Face features -->
          <div class="facemesh-face-features">
            <div class="facemesh-eye facemesh-left-eye"></div>
            <div class="facemesh-center-eye"></div>
            <div class="facemesh-eye facemesh-right-eye"></div>
            <div class="facemesh-mouth"></div>
          </div>
        </div>

        <!-- Progress Bar Loader -->
        <div class="loader-container">
          <div class="loader"></div>
          <p class="mt-3 text-white text-xs text-center">Initializing face detection...</p>
        </div>
      </div>
    </div>

    <!-- Back Button -->
    <button class="back-button" @click="stop_camera">
      <i class="fas fa-arrow-left text-white"></i>
    </button>

    <!-- Camera Elements -->
    <video ref="video" class="camera-feed" autoplay playsinline></video>
    <canvas ref="canvas" class="camera-canvas"></canvas>

    <div class="mask-overlay"></div>

    <button v-if="needsAudioUnlock" 
      @click="unlockAudio" 
      class="btn btn-primary mb-3">
      Tap to Enable Audio Instructions
    </button>

    <!-- Face Overlay -->
    <div v-show="current_step <= STEPS.LOOK_RIGHT" class="face-overlay-left"></div>
    <div v-show="current_step <= STEPS.LOOK_RIGHT" class="face-overlay-right"></div>
    <div class="merged-oval" :class="{
      'show': current_step >= STEPS.MOVE_CLOSER,
      'completed': progress_percentage >= 100
    }"></div>

    <!-- Capture Animation -->
    <div v-if="showCaptureAnimation" class="capture-overlay">
      <div class="check-container">
        <div class="checkmark-circle">
          <svg width="40" height="40" viewBox="0 0 40 40">
            <path class="checkmark" fill="none" d="M10,20 L17,27 L30,13" />
          </svg>
        </div>
        <div class="success-verification-text">liveness verified</div>
      </div>
    </div>

    <!-- Speech Text -->
    <div v-if="!showInstructions || !isLoading" class="speech-text-container">
      <p class="speech-text">{{ spoken_message }}</p>
    </div>

    <!-- Camera Footer -->
    <div class="camera-footer">
      <div class="company-branding">
        <img :src="clientLogo" alt="Company Logo" class="company-logo"> 
        <span class="text-xxs"></span>
      </div>
    </div>
  </div>
</div>
</template>
<script>
import { ref, markRaw } from "vue";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { useToast } from "@/components/Toast/useToast";

export default {
  name: "LivenessVerificationV1",
  components: {
  },
  props: {
    loadingConfig: {
      type: Object,
      default: () => ({
        useCustomLoader: false,
        messages: {
          idCheck: 'Checking ID...',
          biometricsVerification: 'Verifying biometrics...'
        }
      })
    }
  },
  setup() {
    const id_number = ref("");
    const { showError } = useToast();
    
    return {
      id_number,
      showError
    };
  },
  data: () => ({
    isLoading: false,
    photo_taken: false,
    photo_src: "",
    video_stream: null,
    faceLandmarker: null,
    requestAnimationFrameId: null,
    looking_center_history: [],
    is_mobile_view: false,
    showCamera: false,
    showInstructions: false,
    current_step: 0,
    is_captured: false,
    progress_percentage: 0,
    oval_size: 0.18,
    step_start_time: 0,
    has_initial_instruction: false,
    selected_voice: null,
    voice_initialized: false,
    timer_count: 3,
    show_timer: false,
    status_message: "",
    last_spoken_message: '',
    last_spoken_time: 0,
    spoken_message: '',
    showCaptureAnimation: false,
    audio_context: null,
    silent_audio_el: null,
    error_message: "",
    faceMeshInitialized: false,
    STEPS: {
      LOOK_LEFT: 0,
      LOOK_RIGHT: 1,
      MOVE_CLOSER: 2,
      LOOK_CENTER: 3,
      CAPTURE: 4
    },
    STEP_HOLD_TIME: 1200,
    SPEECH_COOLDOWN: 1500,
    LOOKING_CENTER_HOLD_TIME: 2000,
    center_gaze_start_time: 0,
    instruction_message: "",
    status_messages: {
      LOOK_LEFT: "Please turn your head slightly to the left",
      LOOK_RIGHT: "Great! Please turn your head slightly to the right",
      GET_CLOSER: "Please position your face within the oval",
      LOOK_CENTER: "Now look directly into the camera",
      CAPTURE: "Hold still for photo capture",
      MULTIPLE_FACES: "Only one person should be in frame",
      NO_FACE: "No face detected - please center your face",
      PERFECT_POSITION: "Perfect position! Hold still",
      BLINK_REQUIRED: "Please blink naturally",
      BLINK_DETECTED: "Blink detected!",
      LOOKING_CENTER: "Great! Keep looking at the camera",
      MOVE_SLIGHTLY_LEFT: "Please move slightly to the left",
      MOVE_SLIGHTLY_RIGHT: "Please move slightly to the right",
      MOVE_SLIGHTLY_UP: "Please move slightly up",
      MOVE_SLIGHTLY_DOWN: "Please move slightly down",
    },
    is_field_disabled: {},
    blink_detected: false,
    last_blink_time: 0,
    blink_count: 0,
    eye_closed_frames: 0,
    eye_open_frames: 0,
    MIN_EYE_CLOSED_FRAMES: 1,
    MIN_EYE_OPEN_FRAMES: 5,
    EYE_CLOSED_THRESHOLD: 0.019,
    EYE_OPEN_THRESHOLD: 0.02,
    awaiting_blink: false,
  }),
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    clientLogo() {
      const domain = window.location.hostname;
      let logoPath;

      if (domain.includes('wizzit')) {
        logoPath = require('@/assets/images/Wizzit-Logo-Red.svg');
      } else if (domain.includes('vodacom')) {
        logoPath = require('@/assets/images/new_voda.svg');
      } else {
        logoPath = require('@/assets/images/new_black_logo2.svg');
      }

      return logoPath;
    },
    progressStyle() {
      const circumference = 2 * Math.PI * 45;
      const offset = circumference - (this.progress_percentage / 100) * circumference;
      return {
        strokeDasharray: `${circumference} ${circumference}`,
        strokeDashoffset: offset
      };
    },
  },
  methods: {
    async initializeAudioContext() {
      try {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (isIOS) {
          if (!this.audio_context) {
            this.audio_context = new (window.AudioContext || window.webkitAudioContext)();
          }

          if (this.audio_context.state === 'suspended') {
            await this.audio_context.resume();
          }

          // For iOS we need to create a silent audio element and play it
          if (!this.silent_audio_el) {
            this.silent_audio_el = document.createElement('audio');
            this.silent_audio_el.src = 'data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
            this.silent_audio_el.loop = false;
            this.silent_audio_el.setAttribute('playsinline', '');
            this.silent_audio_el.setAttribute('webkit-playsinline', '');

            this.silent_audio_el.addEventListener('ended', () => {
              if (this.silent_audio_el) {
                this.silent_audio_el.src = '';
                try {
                  document.body.removeChild(this.silent_audio_el);
                } catch (e) {
                  // Element might not be in the DOM
                }
                this.silent_audio_el = null;
              }
            });

            document.body.appendChild(this.silent_audio_el);
          }

          try {
            const playPromise = this.silent_audio_el.play();
            if (playPromise !== undefined) {
              playPromise.catch(e => {
                console.warn("Silent audio playback failed:", e);
                this.audio_play_failed = true;
              });
            }
          } catch (e) {
            console.warn("Could not play silent audio:", e);
            this.audio_play_failed = true;
          }
        }

        return true;
      } catch (error) {
        console.error("Audio context initialization failed:", error);
        return false;
      }
    },
    async prepareForExit() {
      if (window.speechSynthesis) {
        speechSynthesis.cancel();
      }

      if (this.silent_audio_el) {
        this.silent_audio_el.pause();
        this.silent_audio_el.src = '';
        try {
          document.body.removeChild(this.silent_audio_el);
        } catch (e) {
          // Element might not be in the DOM
        }
        this.silent_audio_el = null;
      }

      if (this.audio_context) {
        await this.audio_context.close();
        this.audio_context = null;
      }
    },
    async speak(message) {
      const current_time = Date.now();

      if (message !== this.last_spoken_message &&
        current_time - this.last_spoken_time > this.SPEECH_COOLDOWN) {

        this.spoken_message = message;
        this.status_message = message;

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        await this.initializeAudioContext();

        if (isIOS && this.audio_play_failed && this.silent_audio_el) {
          try {
            this.audio_play_failed = false;
            await this.silent_audio_el.play();
          } catch (e) {
            console.warn("Silent audio retry failed:", e);
          }
        }

        if (window.speechSynthesis) {
          if (!this.voice_initialized) {
            await this.initializeVoices();
          }

          if (this.selected_voice) {
            speechSynthesis.cancel();

            const utterance = new SpeechSynthesisUtterance(message);
            utterance.voice = this.selected_voice;
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;

            this.last_spoken_message = message;
            this.last_spoken_time = current_time;

            utterance.onerror = (event) => {
              console.error("Speech synthesis error:", event);
            };

            if (isIOS) {
              setTimeout(() => {
                try {
                  speechSynthesis.pause();
                  speechSynthesis.resume();
                  speechSynthesis.speak(utterance);
                } catch (error) {
                  console.error("Failed to speak on iOS:", error);
                }
              }, 250);
            } else {
              try {
                speechSynthesis.speak(utterance);
              } catch (error) {
                console.error("Failed to speak:", error);
              }
            }
          } else {
            console.warn("Voice not initialized yet");
          }
        } else {
          console.warn("Speech synthesis not available on this browser");
        }
      }
    },
    async unlockAudio() {
      await this.initializeAudioContext();
      await this.resumeAudioContext();
      this.forceSpeechSynthesisInit();
      this.needsAudioUnlock = false;

      this.speak("Audio enabled successfully");
    },
    checkSpeechSynthesis() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS && !this.speechChecked) {
        this.speechChecked = true;
        const testUtterance = new SpeechSynthesisUtterance("Test");

        testUtterance.onend = () => {
          this.speechWorks = true;
        };

        testUtterance.onerror = () => {
          this.speechWorks = false;
          this.needsAudioUnlock = true;
        };

        speechSynthesis.speak(testUtterance);

        setTimeout(() => {
          if (this.speechWorks === undefined) {
            this.needsAudioUnlock = true;
          }
        }, 1000);
      }
    },
    forceSpeechSynthesisInit() {
      if (window.speechSynthesis) {
        speechSynthesis.cancel();

        const initUtterance = new SpeechSynthesisUtterance('');
        initUtterance.volume = 0;
        initUtterance.rate = 1;
        initUtterance.pitch = 1;

        speechSynthesis.speak(initUtterance);
        speechSynthesis.cancel();
      }
    },
    async initializeVoices() {
      return new Promise((resolve) => {
        const getVoices = () => {
          const voices = window.speechSynthesis.getVoices();

          if (voices.length > 0) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

            if (isIOS) {
              this.selected_voice =
                // iOS has specific high-quality voices
                voices.find(voice => voice.name.includes('Samantha')) ||
                voices.find(voice => voice.name.includes('Karen')) ||
                voices.find(voice => voice.name.includes('Moira')) ||
                voices.find(voice => voice.lang === 'en-US') ||
                voices[0]; // Fallback to first voice
            } else {
              this.selected_voice =
                voices.find(voice => voice.name.includes('Zira') || voice.name.includes('Microsoft Zira')) ||
                voices.find(voice =>
                  voice.lang.startsWith('en') &&
                  (voice.name.toLowerCase().includes('female') ||
                    voice.name.includes('woman') ||
                    voice.name.includes('Victoria') ||
                    voice.name.includes('Karen'))
                ) ||
                voices.find(voice =>
                  voice.lang.startsWith('en') &&
                  !voice.name.includes('David') &&
                  !voice.name.toLowerCase().includes('male')
                ) ||
                voices[0];
            }

            this.voice_initialized = true;
            resolve(true);
          } else {
            setTimeout(getVoices, 200);
          }
        };

        if (window.speechSynthesis.onvoiceschanged !== undefined) {
          window.speechSynthesis.onvoiceschanged = getVoices;
        }

        getVoices();
      });
    },
    show_instructions() {
      this.showInstructions = true;
      this.showCamera = false;
    },
    async initializeFaceLandmarker() {
      this.isLoading = true;
      this.faceMeshInitialized = false;

      try {
        const faceLandmarkerReady = new Promise((resolve, reject) => {
          FilesetResolver.forVisionTasks(
            "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
          )
            .then(vision => {
              return FaceLandmarker.createFromOptions(vision, {
                baseOptions: {
                  modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
                  delegate: "GPU"
                },
                outputFaceBlendshapes: true,
                runningMode: "VIDEO",
                numFaces: 2,
                minFaceDetectionConfidence: 0.7,
                minFacePresenceConfidence: 0.7,
                minTrackingConfidence: 0.7
              });
            })
            .then(faceLandmarker => {
              this.faceLandmarker = markRaw(faceLandmarker);
              this.faceMeshInitialized = true;
              resolve();
            })
            .catch(error => {
              console.error("[LivenessCheck] Error initializing FaceLandmarker:", error); // DEBUG
              reject(error);
            });
        });

        const timeoutPromise = new Promise((_, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('FaceLandmarker initialization timeout'));
          }, 10000);

          faceLandmarkerReady.then(() => clearTimeout(timeoutId));
        });

        await Promise.race([
          faceLandmarkerReady,
          timeoutPromise
        ]);

        if (this.faceMeshInitialized) {
          this.predictWebcam();
          this.speak(
            "Face detection system initialized... Please look slightly to the left"
          );
          this.updateStatusMessage(this.status_messages.LOOK_LEFT);
        }
      } catch (error) {
        console.error("Face detection setup failed:", error);
        this.error_message = "Failed to initialize face detection. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    set_id_number(id) {
      this.id_number = id;
    },
    checkGazeDirection(landmarks) {
      const left_eye = landmarks[33];
      const right_eye = landmarks[263];
      const nose = landmarks[1];

      const eye_distance = right_eye.x - left_eye.x;
      const nose_position = (nose.x - left_eye.x) / eye_distance;

      const left_overlay = document.querySelector('.face-overlay-left');
      const right_overlay = document.querySelector('.face-overlay-right');
      const merged_oval = document.querySelector('.merged-oval');

      let is_correct_position = false;
      let progress = 0;

      switch (this.current_step) {
        case this.STEPS.LOOK_LEFT:
          progress = Math.min(Math.max((nose_position - 0.5) / 0.2, 0), 1) * 100;

          if (merged_oval) {
            merged_oval.style.opacity = '0';
            merged_oval.classList.remove('show');
          }

          if (progress >= 100) {
            left_overlay.classList.add('completed');
            is_correct_position = true;
            if (!this.step_start_time) {
              this.step_start_time = Date.now();
            }
          } else {
            left_overlay.classList.remove('completed');
            this.step_start_time = 0;
          }

          left_overlay.style.setProperty('--progress-percent', `${progress}%`);
          break;

        case this.STEPS.LOOK_RIGHT:
          progress = Math.min(Math.max((0.5 - nose_position) / 0.2, 0), 1) * 100;

          if (progress >= 100) {
            right_overlay.classList.add('completed');
            is_correct_position = true;
            if (!this.step_start_time) {
              this.step_start_time = Date.now();
            }

            if (left_overlay.classList.contains('completed')) {
              left_overlay.classList.add('merge');
              right_overlay.classList.add('merge');
              if (merged_oval) {
                merged_oval.classList.add('show');
                merged_oval.style.opacity = '1';
              }
            }
          } else {
            right_overlay.classList.remove('completed');
            this.step_start_time = 0;
          }

          right_overlay.style.setProperty('--progress-percent', `${progress}%`);
          break;
        case this.STEPS.LOOK_CENTER:
          is_correct_position = nose_position >= 0.45 && nose_position <= 0.55;
          break;
      }

      if (is_correct_position &&
        this.step_start_time &&
        Date.now() - this.step_start_time >= this.STEP_HOLD_TIME) {
        this.progressToNextStep();
      }

      return is_correct_position;
    },
    checkFaceFit(landmarks) {
      const centerX = this.$refs.canvas.width / 2;
      const centerY = this.$refs.canvas.height / 2;
      const radiusX = this.$refs.canvas.width * this.oval_size;
      const radiusY = this.$refs.canvas.height * (this.oval_size * 1.67);

      const facePoints = [
        landmarks[10],   // Forehead
        landmarks[152],  // Chin
        landmarks[234],  // Left cheek
        landmarks[454],  // Right cheek
        landmarks[1],    // Nose
        landmarks[33],   // Left eye
        landmarks[263],  // Right eye
        landmarks[61],   // Mouth left
        landmarks[291]   // Mouth right
      ];

      const merged_oval = document.querySelector('.merged-oval');
      const face_width = Math.abs(landmarks[234].x - landmarks[454].x) * this.$refs.canvas.width;
      const min_required_width = this.is_mobile_view
        ? this.$refs.canvas.width * 0.40
        : this.$refs.canvas.width * 0.20;

      const is_looking_center = this.checkLookingCenter(landmarks);

      if (face_width < min_required_width) {
        this.speak('Please move closer to the camera');
        this.updateStatusMessage(this.status_messages.GET_CLOSER);
        this.progress_percentage = Math.max(0, this.progress_percentage - 2);

        if (merged_oval) {
          merged_oval.style.setProperty('--progress-percent', `${this.progress_percentage}%`);
          merged_oval.classList.remove('completed');
        }
        return false;
      }

      const isInOval = facePoints.every(point => {
        const x = (point.x * this.$refs.canvas.width - centerX) / radiusX;
        const y = (point.y * this.$refs.canvas.height - centerY) / radiusY;
        return (x * x + y * y) <= 1;
      });

      const noseX = landmarks[1].x * this.$refs.canvas.width;
      const noseY = landmarks[1].y * this.$refs.canvas.height;
      const is_centered = Math.abs(noseX - centerX) < radiusX * 0.2 &&
        Math.abs(noseY - centerY) < radiusY * 0.2;

      const all_conditions_met = isInOval && is_centered && is_looking_center;

      if (!all_conditions_met) {
        this.progress_percentage = Math.max(0, this.progress_percentage - 2);

        if (this.current_step !== this.STEPS.LOOK_CENTER) {
          if (!is_looking_center) {
            this.speak('Please look directly at the camera');
            this.updateStatusMessage('Please look directly at the camera');
          } else if (noseX < centerX - radiusX * 0.2) {
            this.speak('Please move slightly to the right');
            this.updateStatusMessage(this.status_messages.MOVE_SLIGHTLY_RIGHT);
          } else if (noseX > centerX + radiusX * 0.2) {
            this.speak('Please move slightly to the left');
            this.updateStatusMessage(this.status_messages.MOVE_SLIGHTLY_LEFT);
          } else if (noseY < centerY - radiusY * 0.2) {
            this.speak('Please move slightly down');
            this.updateStatusMessage(this.status_messages.MOVE_SLIGHTLY_DOWN);
          } else if (noseY > centerY + radiusY * 0.2) {
            this.speak('Please move slightly up');
            this.updateStatusMessage(this.status_messages.MOVE_SLIGHTLY_UP);
          }
        }
      } else {
        this.progress_percentage = Math.min(100, this.progress_percentage + 2);

        if (this.progress_percentage >= 90 &&
          (this.current_step !== this.STEPS.CAPTURE && this.current_step !== this.STEPS.LOOK_CENTER)) {
          this.updateStatusMessage(this.status_messages.PERFECT_POSITION);
        }
      }

      if (merged_oval && this.current_step >= this.STEPS.MOVE_CLOSER) {
        merged_oval.style.setProperty('--progress-percent', `${this.progress_percentage}%`);

        if (this.progress_percentage >= 100) {
          merged_oval.classList.add('completed');
        } else {
          merged_oval.classList.remove('completed');
        }
      }

      return all_conditions_met && this.progress_percentage === 100;
    },
    updateStatusMessage(message) {
      this.status_message = message;
      this.instruction_message = this.status_messages[this.current_step] || "";
    },
    progressToNextStep() {
      if (this.current_step === this.STEPS.MOVE_CLOSER && this.progress_percentage < 100) {
        return;
      }

      this.step_start_time = 0;
      this.current_step++;
      this.center_gaze_start_time = 0;

      const left_overlay = document.querySelector('.face-overlay-left');
      const right_overlay = document.querySelector('.face-overlay-right');
      const merged_oval = document.querySelector('.merged-oval');

      switch (this.current_step) {
        case this.STEPS.LOOK_RIGHT:
          if (right_overlay) {
            right_overlay.style.setProperty('--progress-percent', '0%');
            right_overlay.classList.remove('completed');
          }
          this.speak('Great! Please turn your head slightly to the right');
          this.updateStatusMessage(this.status_messages.LOOK_RIGHT);
          break;

        case this.STEPS.MOVE_CLOSER:
          if (left_overlay && right_overlay) {
            left_overlay.classList.add('completed');
            right_overlay.classList.add('completed');

            setTimeout(() => {
              left_overlay.classList.add('fade-out');
              right_overlay.classList.add('fade-out');

              setTimeout(() => {
                if (merged_oval) {
                  merged_oval.classList.remove('hidden');
                  merged_oval.classList.add('show');
                }
              }, 600);
            }, 100);
          }

          this.speak('Excellent! Please look into the camera, and move closer');
          this.updateStatusMessage(this.status_messages.GET_CLOSER);
          this.oval_size = 0.25;
          break;

        case this.STEPS.LOOK_CENTER:
          if (merged_oval) {
            merged_oval.classList.remove('completed');
            merged_oval.classList.remove('hidden');
            merged_oval.classList.add('show');
          }
          this.speak('Perfect! Now look directly into the camera');
          this.updateStatusMessage(this.status_messages.LOOK_CENTER);
          break;

        case this.STEPS.CAPTURE:
          this.updateStatusMessage(this.status_messages.CAPTURE);
          break;
      }
    },
    checkLookingCenter(landmarks) {
      // Get eye landmarks
      const left_eye_left_corner = landmarks[33];
      const left_eye_right_corner = landmarks[133];
      const right_eye_left_corner = landmarks[362];
      const right_eye_right_corner = landmarks[263];

      // Get iris landmarks
      const left_iris_center = landmarks[468];
      const right_iris_center = landmarks[473];

      // Calculate relative positions of irises within eye sockets
      const left_eye_width = left_eye_right_corner.x - left_eye_left_corner.x;
      const right_eye_width = right_eye_right_corner.x - right_eye_left_corner.x;

      const left_iris_relative_x = (left_iris_center.x - left_eye_left_corner.x) / left_eye_width;
      const right_iris_relative_x = (right_iris_center.x - right_eye_left_corner.x) / right_eye_width;

      // Get vertical position
      const left_eye_top = landmarks[159];
      const left_eye_bottom = landmarks[145];
      const right_eye_top = landmarks[386];
      const right_eye_bottom = landmarks[374];

      const left_eye_height = left_eye_bottom.y - left_eye_top.y;
      const right_eye_height = right_eye_bottom.y - right_eye_top.y;

      const left_iris_relative_y = (left_iris_center.y - left_eye_top.y) / left_eye_height;
      const right_iris_relative_y = (right_iris_center.y - right_eye_top.y) / right_eye_height;

      const horizontal_threshold = {
        min: 0.3,
        max: 0.65
      };

      const vertical_threshold = {
        min: 0.2,
        max: 0.8
      };

      const tolerance = 0.15;

      // Check if irises are centered horizontally (looking straight)
      const is_looking_straight_h =
        (left_iris_relative_x >= horizontal_threshold.min - tolerance &&
          left_iris_relative_x <= horizontal_threshold.max + tolerance) &&
        (right_iris_relative_x >= horizontal_threshold.min - tolerance &&
          right_iris_relative_x <= horizontal_threshold.max + tolerance);

      // Check if irises are centered vertically (not looking up/down)
      const is_looking_straight_v =
        (left_iris_relative_y >= vertical_threshold.min - tolerance &&
          left_iris_relative_y <= vertical_threshold.max + tolerance) &&
        (right_iris_relative_y >= vertical_threshold.min - tolerance &&
          right_iris_relative_y <= vertical_threshold.max + tolerance);

      if (!this.looking_center_history) {
        this.looking_center_history = [];
      }

      const current_result = is_looking_straight_h && is_looking_straight_v;
      this.looking_center_history.push(current_result);

      if (this.looking_center_history.length > 10) {
        this.looking_center_history.shift();
      }

      const true_count = this.looking_center_history.filter(Boolean).length;
      return true_count >= Math.ceil(this.looking_center_history.length * 0.4);
    },
    onResults(results) {
      if (this.is_captured) return;

      const merged_oval = document.querySelector('.merged-oval');

      if (merged_oval && merged_oval.classList.contains('capture-ready')) {
        merged_oval.classList.remove('capture-ready');
      }

      if (results.faceLandmarks?.length > 0) {
        const numFaces = results.faceLandmarks.length;
        if (numFaces > 1) {
          this.speak('Please ensure only one person is in frame');
          this.updateStatusMessage(this.status_messages.MULTIPLE_FACES);
          return;
        } else if (numFaces === 1) {
          if (this.status_message.includes('No face') ||
            this.status_message.includes('Multiple faces')) {
            switch (this.current_step) {
              case this.STEPS.LOOK_LEFT:
                this.speak(this.status_messages.LOOK_LEFT);
                this.updateStatusMessage(this.status_messages.LOOK_LEFT);
                break;
              case this.STEPS.LOOK_RIGHT:
                this.speak(this.status_messages.LOOK_RIGHT);
                this.updateStatusMessage(this.status_messages.LOOK_RIGHT);
                break;
              case this.STEPS.MOVE_CLOSER:
                this.speak(this.status_messages.GET_CLOSER);
                this.updateStatusMessage(this.status_messages.GET_CLOSER);
                break;
              case this.STEPS.LOOK_CENTER:
                this.speak(this.status_messages.LOOK_CENTER);
                this.updateStatusMessage(this.status_messages.LOOK_CENTER);
                break;
              case this.STEPS.CAPTURE:
                this.speak(this.status_messages.CAPTURE);
                this.updateStatusMessage(this.status_messages.CAPTURE);
                break;
            }
          }

          const landmarks = results.faceLandmarks[0];

          if (this.current_step <= this.STEPS.LOOK_RIGHT) {
            const gaze_correct = this.checkGazeDirection(landmarks);
            if (gaze_correct && !this.step_start_time) {
              this.step_start_time = Date.now();
            } else if (gaze_correct && Date.now() - this.step_start_time >= this.STEP_HOLD_TIME) {
              this.progressToNextStep();
            }
          } else if (this.current_step === this.STEPS.MOVE_CLOSER) {
            if (this.checkFaceFit(landmarks)) {
              this.progressToNextStep();
            }
          } else if (this.current_step === this.STEPS.LOOK_CENTER) {
            const is_looking_center = this.checkGazeDirection(landmarks);
            const is_face_fit = this.checkFaceFit(landmarks);

            if (is_looking_center && is_face_fit) {
              if (!this.center_gaze_start_time) {
                this.center_gaze_start_time = Date.now();
                this.speak('Great! look at the camera');
                this.updateStatusMessage(this.status_messages.LOOKING_CENTER);
              } else if (Date.now() - this.center_gaze_start_time >= this.LOOKING_CENTER_HOLD_TIME) {
                this.progressToNextStep();
              }
            } else {
              this.center_gaze_start_time = 0;
            }
          } else if (this.current_step === this.STEPS.CAPTURE) {
            const is_looking_center = this.checkLookingCenter(landmarks);
            const is_face_fit = this.checkFaceFit(landmarks);

            if (is_looking_center && is_face_fit) {
              if (merged_oval) {
                merged_oval.classList.add('capture-ready');
              }
              this.take_photo();
            }
          }
        }
      } else {
        this.speak('No face detected');
        this.updateStatusMessage(this.status_messages.NO_FACE);
      }
    },
    async predictWebcam() {
      if (!this.showCamera || !this.faceLandmarker) {
        if (this.showCamera) {
          this.requestAnimationFrameId = requestAnimationFrame(this.predictWebcam.bind(this));
        }
        return;
      }

      const video = this.$refs.video;
      if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA ||
        video.videoWidth === 0 || video.videoHeight === 0) {
        if (this.showCamera) {
          this.requestAnimationFrameId = requestAnimationFrame(this.predictWebcam.bind(this));
        }
        return;
      }

      try {
        const startTimeMs = performance.now();
        const results = this.faceLandmarker.detectForVideo(video, startTimeMs);
        if (results && results.faceLandmarks) {
          this.onResults(results);
        }
      } catch (error) {
        console.error("Error in face detection:", error);
        if (error.message && error.message.includes('runningMode')) {
          console.warn("Attempting to reinitialize FaceLandmarker with correct mode");
          await this.initializeFaceLandmarker();
        }
      }

      if (this.showCamera) {
        this.requestAnimationFrameId = requestAnimationFrame(this.predictWebcam.bind(this));
      }
    },
    initialize_camera() {
      if (this.video_stream) return;

      this.showCamera = true;

      const is_mobile = window.innerWidth <= 768;

      const constraints = {
        video: {
          width: { ideal: is_mobile ? 640 : 1280 },
          height: { ideal: is_mobile ? 480 : 720 },
          facingMode: "user"
        }
      };

      navigator.mediaDevices.getUserMedia(constraints)
        .then(stream => {
          const video = this.$refs.video;
          video.srcObject = stream;
          this.video_stream = stream;

          video.onloadedmetadata = async () => {
            video.play();

            if (this.$refs.canvas) {
              this.$refs.canvas.width = video.videoWidth;
              this.$refs.canvas.height = video.videoHeight;
            }

            if (this.faceLandmarker) {
              try {
                await this.faceLandmarker.close();
              } catch (e) {
                console.warn("Error closing faceLandmarker:", e);
              }
              this.faceLandmarker = null;
            }

            await this.initializeFaceLandmarker();
          };
        })
        .catch((error) => {
          console.error("Error accessing camera:", error);
          this.showError(
            "Camera Access Error",
            "We couldn't access your camera. Please ensure you've given permission and try again."
          );
        });
    },
    handle_camera_resize() {
      if (this.$refs.video && this.$refs.canvas) {
        this.$refs.canvas.width = this.$refs.video.videoWidth;
        this.$refs.canvas.height = this.$refs.video.videoHeight;
      }
    },
    handle_window_resize() {
      this.toggle_photo_sections();
      this.handle_camera_resize();

      this.is_mobile_view = window.innerWidth <= 768;
    },
    closeInstructions() {
      this.showInstructions = false;
    },
    async stop_camera() {
      try {
        if (window.speechSynthesis) {
          speechSynthesis.cancel();
        }

        if (this.requestAnimationFrameId) {
          cancelAnimationFrame(this.requestAnimationFrameId);
          this.requestAnimationFrameId = null;
        }

        if (this.faceLandmarker) {
          try {
            await this.faceLandmarker.close();
          } catch (e) {
            console.warn("Error closing faceLandmarker:", e);
          }
          this.faceLandmarker = null;
        }

        if (this.silent_audio_el) {
          this.silent_audio_el.pause();
          this.silent_audio_el.src = '';
          try {
            document.body.removeChild(this.silent_audio_el);
          } catch (e) {
            // Element might not be in the DOM
          }
          this.silent_audio_el = null;
        }

        if (this.audio_context) {
          await this.audio_context.close();
          this.audio_context = null;
        }

        this.last_spoken_message = '';
        this.last_spoken_time = 0;
        this.voice_initialized = false;
        this.selected_voice = null;

        if (this.video_stream) {
          this.video_stream.getTracks().forEach(track => track.stop());
          this.video_stream = null;
        }
        if (this.$refs.video) {
          this.$refs.video.srcObject = null;
        }

        this.showCamera = false;
        this.showInstructions = false;
        this.is_captured = false;
        this.faceMeshInitialized = false;
        this.isLoading = false;
        this.error_message = "";
        this.$emit('camera-closed');
      } catch (error) {
        console.error("Error stopping camera and audio:", error);
      }
    },
    toggle_photo_sections() {
      this.is_mobile_view = window.innerWidth <= 768;
      if (this.is_mobile_view) {
        $("#take_photo").show();
        $("#submit_photo").hide();
      } else {
        $("#take_photo").show();
        $("#submit_photo").show();
      }
    },
    take_photo() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      this.photo_taken = true;
      this.photo_src = canvas.toDataURL();
      this.is_captured = true;

      this.showCaptureAnimation = true;

      setTimeout(() => {
        this.$emit('photo-captured', this.photo_src);
      }, 5000);

      if (this.is_mobile_view) {
        $("#take_photo").hide();
        $("#submit_photo").show();
        $(".retake-photo").removeClass("d-none");
      }
    },
    close_modal() {
      this.stop_camera();
      this.$emit('close');
    },
    async resumeAudioContext() {
      if (this.audio_context && this.audio_context.state === 'suspended') {
        await this.audio_context.resume();
      }
    },
    async startFaceRecognition() {
      await this.initializeAudioContext();

      this.showInstructions = false;
      this.showCamera = true;

      if (!this.video_stream) {
        this.initialize_camera();
      } else if (this.faceLandmarker && !this.requestAnimationFrameId) {
        this.predictWebcam();
      } else if (!this.faceLandmarker) {
        await this.initializeFaceLandmarker();
      }
    }
  },
  watch: {
    faceMeshInitialized: {
      immediate: true,
      handler(newValue) {
        if (newValue === true) {
          this.isLoading = false;
        }
      }
    },
    showCamera(newVal) {
      this.$store.commit('SET_CAMERA_STATE', newVal)
    }
  },
  mounted() {
    this.initializeAudioContext();
    this.initializeVoices();

    const shown_instructions = localStorage.getItem("shown_instructions");

    if (shown_instructions) {
      this.showCamera = true;
      this.initialize_camera();
    } else {
      this.show_instructions();
    }

    window.addEventListener("resize", this.handle_window_resize);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.prepareForExit);
    window.removeEventListener('pagehide', this.prepareForExit);
    this.stop_camera();
    window.removeEventListener("resize", this.handle_window_resize);
  },
  beforeRouteLeave(to, from, next) {
    this.stop_camera();
    next();
  }
};
</script>